// Cache for durations to avoid refetching the same URL
const durationCache = new Map<string, number>();

export function getAudioDurationFromUrl(src: string): Promise<number> {
  // Check cache first
  if (durationCache.has(src)) {
    return Promise.resolve(durationCache.get(src)!);
  }

  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';

    const cleanup = () => {
      if (audio.src.startsWith('blob:')) {
        window.URL.revokeObjectURL(audio.src);
      }
    };

    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      // Cache the duration
      durationCache.set(src, duration);
      cleanup();
      resolve(duration);
    };

    audio.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load audio metadata from: ${src}`));
    };

    audio.src = src;
  });
}

export function getAudioDuration(file: File): Promise<number> {
  if (!file.type.startsWith('audio/')) {
    return Promise.reject(new Error(`Invalid audio file type: ${file.type}`));
  }

  const audioUrl = URL.createObjectURL(file);
  return getAudioDurationFromUrl(audioUrl);
}

export async function getTotalAudioDuration(files: File[]): Promise<number> {
  try {
    const durations = await Promise.all(files.map(getAudioDuration));
    return durations.reduce((acc, duration) => acc + duration, 0);
  } catch (error) {
    throw new Error(
      `Failed to calculate total audio duration: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
